import { COLLISION_CELLS_PER_TILE, type WorldDocument } from "../document/types.ts";
import type { CellPatchIntent, Intent } from "./op-types.ts";
import { CLAIMS_PER_EDITOR, CLAIMS_PER_WORLD, type ClaimTarget, type LiveClaim } from "./protocol-stream.ts";
import type { LiveServerMessage } from "./protocol.ts";
import { layerOf, mapOf } from "./resolve-types.ts";
import { CLAIM_GONE_MS, CLAIM_IDLE_MS, type RoomClaim, type RoomSender, type RoomState, type RoomStep } from "./room-types.ts";

/**
 * Soft locks in a room (ADR-086 decision 29): a builder claims a layer or a rectangle of tiles, the room tells
 * every tab who holds what, and a claim ends when it is released, `CLAIM_GONE_MS` after its holder's socket
 * left, and `CLAIM_IDLE_MS` after the last operation of its holder inside it. A claim stops nothing: the room
 * applies an operation inside another builder's claim like any other. Claims live in the room's memory alone.
 */

/**
 * A rectangle of tiles.
 */
type TileRect = Extract<ClaimTarget, { readonly x: number }>;

/**
 * Whether two targets are the same layer or the same rectangle.
 *
 * @param a One target.
 * @param b The other.
 * @returns True when they cover the same thing.
 */
function sameTarget(a: ClaimTarget, b: ClaimTarget): boolean {
  if ("layer" in a || "layer" in b) {
    return "layer" in a && "layer" in b && a.map === b.map && a.layer === b.layer;
  }
  return a.map === b.map && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * Whether a point in tiles lies in a rectangle of tiles.
 *
 * @param x The point's x.
 * @param y The point's y.
 * @param rect The rectangle.
 * @returns True when it does; the rectangle's right and bottom edges are outside it.
 */
function holds(rect: TileRect, x: number, y: number): boolean {
  return x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h;
}

/**
 * Whether a cell patch writes a cell that lies in a rectangle of tiles: a tile cell is its tile, a collision, zone
 * or door cell lies in the tile it subdivides, and a corner belongs to each of the four tiles it touches.
 *
 * @param document The document the patch is aimed at.
 * @param intent The patch.
 * @param rect The rectangle.
 * @returns True when a cell does; false when the layer is gone or holds another grid.
 */
function patchTouches(document: WorldDocument, intent: CellPatchIntent, rect: TileRect): boolean {
  const map = mapOf(document, intent.map);
  const layer = layerOf(document, intent.map, intent.layer);
  const kinds = { corners: "terrain", collision: "collision", zone: "zone", tiles: "tile", door: "door" } as const;
  if (map === undefined || layer === undefined || layer.kind !== kinds[intent.grid]) {
    return false;
  }
  const scale = intent.grid === "collision" || intent.grid === "zone" || intent.grid === "door" ? COLLISION_CELLS_PER_TILE : 1;
  const width = intent.grid === "corners" ? map.width + 1 : map.width * scale;
  const reach = intent.grid === "corners" ? 1 : 0;
  const left = rect.x * scale;
  const right = (rect.x + rect.w) * scale - 1 + reach;
  const top = rect.y * scale;
  const bottom = (rect.y + rect.h) * scale - 1 + reach;
  for (const [start, length] of intent.cells) {
    const end = start + length - 1;
    const first = Math.floor(start / width);
    const last = Math.floor(end / width);
    for (let row = Math.max(first, top); row <= Math.min(last, bottom); row += 1) {
      const from = row === first ? start % width : 0;
      const to = row === last ? end % width : width - 1;
      if (from <= right && to >= left) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Where an item of a layer stands.
 *
 * @param document The document.
 * @param mapId The map.
 * @param layerId The layer.
 * @param id The prop's or the entity's id.
 * @returns Its position in tiles, or undefined when it is gone.
 */
function standing(document: WorldDocument, mapId: string, layerId: string, id: string): { readonly x: number; readonly y: number } | undefined {
  const layer = layerOf(document, mapId, layerId);
  const items = layer?.kind === "prop" ? layer.props : layer?.kind === "entity" ? layer.entities : [];
  return items.find((item) => item.id === id);
}

/**
 * Whether an intent works inside what a claim covers. A layer is touched by every intent that names its map
 * and the layer itself: its cells, its fields, its items, its place, its add and its removal. A rectangle is
 * touched by a patch that writes a cell lying in it, and by an item that stands in it, is put into it or
 * moves out of it; nothing else is inside a rectangle.
 *
 * @param document The document the intent is aimed at, before it applies.
 * @param intent The intent.
 * @param target What the claim covers.
 * @returns True when the intent is inside it.
 */
export function intentTouches(document: WorldDocument, intent: Intent, target: ClaimTarget): boolean {
  if (!("map" in intent) || intent.k === "addMap" || intent.map !== target.map) {
    return false;
  }
  if ("layer" in target) {
    return "layer" in intent && (intent.k === "addLayer" ? intent.layer.id : intent.layer) === target.layer;
  }
  switch (intent.k) {
    case "cells":
      return patchTouches(document, intent, target);
    case "addProp":
      return holds(target, intent.prop.x, intent.prop.y);
    case "addEntity":
      return holds(target, intent.entity.x, intent.entity.y);
    case "updateProp":
    case "updateEntity":
    case "removeProp":
    case "removeEntity": {
      const at = standing(document, intent.map, intent.layer, intent.k === "updateProp" || intent.k === "removeProp" ? intent.prop : intent.entity);
      const moved = (intent.k === "updateProp" || intent.k === "updateEntity") && intent.at !== undefined && holds(target, intent.at[0], intent.at[1]);
      return moved || (at !== undefined && holds(target, at.x, at.y));
    }
    default:
      return false;
  }
}

/**
 * The claims of a room as it tells them.
 *
 * @param claims The claims.
 * @returns The `claims` message: each claim by its holder's socket id and name and what it covers, never the
 *   holder's key.
 */
export function claimsMessage(claims: readonly RoomClaim[]): LiveServerMessage {
  return { t: "claims", list: claims.map((held): LiveClaim => ({ by: held.by, name: held.name, ...held.target })) };
}

/**
 * A step that changed the claims: the room with them, told to every tab.
 *
 * @param state The room.
 * @param claims The claims now.
 * @returns The step.
 */
function told(state: RoomState, claims: readonly RoomClaim[]): RoomStep {
  return { state: { ...state, claims }, send: [{ to: "all", message: claimsMessage(claims) }], refused: null, entry: null };
}

/**
 * Takes a claim for an editor. A claim the editor already holds is counted as work inside it and nothing is
 * told. A claim the same sender's key held on a socket that has left passes to this socket, so a tab that
 * reconnects and states its claims again holds each once; it still counts towards the editor's own limit.
 *
 * @param state The room; never written into.
 * @param sender The editor.
 * @param target What to claim.
 * @param now The time.
 * @returns The room with the claim and `claims` for every tab; or the room as given with `editor-claims` when
 *   the editor holds `CLAIMS_PER_EDITOR`, or `world-claims` when the room holds `CLAIMS_PER_WORLD`.
 */
export function takeClaim(state: RoomState, sender: RoomSender, target: ClaimTarget, now: number): RoomStep {
  const own = state.claims.findIndex((held) => held.by === sender.id && sameTarget(held.target, target));
  if (own >= 0) {
    return { state: { ...state, claims: state.claims.map((held, i) => (i === own ? { ...held, touched: now } : held)) }, send: [], refused: null, entry: null };
  }
  if (state.claims.filter((held) => held.by === sender.id).length >= CLAIMS_PER_EDITOR) {
    return { state, send: [], refused: "editor-claims", entry: null };
  }
  const taken: RoomClaim = { by: sender.id, key: sender.key, name: sender.name, target, touched: now, left: null };
  const former = state.claims.findIndex((held) => held.left !== null && held.key === sender.key && sameTarget(held.target, target));
  if (former >= 0) {
    return told(state, state.claims.map((held, i) => (i === former ? taken : held)));
  }
  if (state.claims.length >= CLAIMS_PER_WORLD) {
    return { state, send: [], refused: "world-claims", entry: null };
  }
  return told(state, [...state.claims, taken]);
}

/**
 * Releases a claim an editor holds.
 *
 * @param state The room; never written into.
 * @param sender The editor.
 * @param target What the claim covers.
 * @returns The room without the claim and `claims` for every tab, or the room as given with `no-claim` when
 *   the editor holds no claim on exactly that.
 */
export function releaseClaim(state: RoomState, sender: RoomSender, target: ClaimTarget): RoomStep {
  const own = state.claims.findIndex((held) => held.by === sender.id && sameTarget(held.target, target));
  return own < 0 ? { state, send: [], refused: "no-claim", entry: null } : told(state, state.claims.filter((_, i) => i !== own));
}

/**
 * Notes that an editor's socket left: its claims stay, shown as they were, for `CLAIM_GONE_MS`.
 *
 * @param state The room; never written into.
 * @param editor The socket's id.
 * @param now The time.
 * @returns The room, the one given when the editor holds no claim.
 */
export function editorLeft(state: RoomState, editor: string, now: number): RoomState {
  return state.claims.some((held) => held.by === editor && held.left === null) ? { ...state, claims: state.claims.map((held) => (held.by === editor && held.left === null ? { ...held, left: now } : held)) } : state;
}

/**
 * Ends every claim whose time has come: its holder's socket left `CLAIM_GONE_MS` ago, or its holder has not
 * worked inside it for `CLAIM_IDLE_MS`.
 *
 * @param state The room; never written into.
 * @param now The time.
 * @returns The room without them and `claims` for every tab, or the room as given when none ended.
 */
export function lapseClaims(state: RoomState, now: number): RoomStep {
  const kept = state.claims.filter((held) => now - held.touched < CLAIM_IDLE_MS && (held.left === null || now - held.left < CLAIM_GONE_MS));
  return kept.length === state.claims.length ? { state, send: [], refused: null, entry: null } : told(state, kept);
}

/**
 * The claim of another editor an operation would work inside (ADR-099 decision 19): what a sender held to the
 * soft locks is refused on, naming the holder. The room never refuses a tab's operation for a claim; a host
 * asks this only for a sender it holds to them.
 *
 * @param state The room, as the operations ahead of this one left it.
 * @param sender The sending editor's id; its own claims never refuse it.
 * @param intents The operation's intents.
 * @returns The first claim, in the room's order, that an intent touches, or null when none does.
 */
export function claimedAgainst(state: RoomState, sender: string, intents: readonly Intent[]): RoomClaim | null {
  return state.claims.find((held) => held.by !== sender && intents.some((intent) => intentTouches(state.document, intent, held.target))) ?? null;
}

/**
 * The claims after an editor's operation: each claim of the editor's that an applied intent worked inside is
 * counted from now.
 *
 * @param claims The claims.
 * @param editor The socket the operation came from.
 * @param document The document before the operation.
 * @param intents The intents that applied.
 * @param now The time.
 * @returns The claims, the list given when none was worked in.
 */
export function claimsWorked(claims: readonly RoomClaim[], editor: string, document: WorldDocument, intents: readonly Intent[], now: number): readonly RoomClaim[] {
  const inside = claims.map((held) => held.by === editor && intents.some((intent) => intentTouches(document, intent, held.target)));
  return inside.some(Boolean) ? claims.map((held, i) => (inside[i] === true ? { ...held, touched: now } : held)) : claims;
}
